import { isPlatformBrowser } from '@angular/common';
import {
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Brand } from '@features/brand/interfaces/brand.interface';
import { BrandService } from '@features/brand/services/brand.service';
import { Category } from '@features/category/interfaces/category.interface';
import { CategoryService } from '@features/category/services/category.service';
import { Product } from '@features/product/interfaces/product.interface';
import { ProductService } from '@features/product/services/product.service';
import {
  faChevronDown,
  faFilter,
  faSortAmountDown,
  faSortAmountUp,
  faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';
import { SortDirection } from '@shared/enums/common.enum';
import { Filter, Sort } from '@shared/interfaces/common.interface';
import { ProductDataListParameter } from '@shared/interfaces/data-list-parameter.interface';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { LayoutService } from 'src/app/layout/services/layout.service';

@Component({
  selector: 'fc-product-list',
  templateUrl: './product-list.component.html',
  styleUrls: ['./product-list.component.css'],
})
export class ProductListComponent implements OnInit, OnDestroy {
  // fontawesome icon
  faTimesCircle = faTimesCircle;
  faSortAmountUp = faSortAmountUp;
  faSortAmountDown = faSortAmountDown;
  faChevronDown = faChevronDown;
  faFilter = faFilter;

  // subject
  private readonly destroy$ = new Subject<void>();

  // handler
  loading: boolean = false;

  // paginations
  filteredItems: any;
  queryParams: any = {};
  totalProducts: number = 0;
  currentPage: number = 1;
  filters: Filter[] = [];

  // sort
  sorts: Sort[] = [];
  sortDropdown: Sort[] = [
    { label: 'Best Selling', field: null, direction: null },
    {
      label: 'Newest Products',
      field: 'products.created_at',
      direction: SortDirection.desc,
    },
    { label: 'Rating', field: 'average_rating', direction: SortDirection.desc },
    {
      label: 'Price High to Low',
      field: 'max_price',
      direction: SortDirection.desc,
    },
    {
      label: 'Price Low to High',
      field: 'max_price',
      direction: SortDirection.asc,
    },
  ];
  selectedSort: Sort = { label: 'Sort By', field: null, direction: null };

  sale: boolean = false;

  // category
  categories: Category[] = [];
  category_id: number = 0;
  selectedCategorySlugs: string[] = [];
  selectedCategoryId?: number;

  // brand
  brands: Brand[] = [];
  selectedBrandIds: { id: number }[] = [];
  brandSelectionInterval: any;

  // price
  selectedPriceRange: { minPrice: number | null; maxPrice: number | null } = {
    minPrice: null,
    maxPrice: null,
  };

  // product
  products: Product[] = [];

  // shop
  @Input() shopSlug: string = '';
  isShop = false;

  // rating
  filterRating: boolean = false;

  // recomendation
  myRecommendation: boolean = false;

  // best sell
  bestSelling: boolean = false;

  // mobile product list
  @ViewChild('productListContainer') productListContainer?: ElementRef;
  @ViewChild('productList') productList?: ElementRef<any>;
  onShowMore: boolean = false;
  mCurrentPage: number = 1;

  // lifecycle hooks
  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private productService: ProductService,
    private brandService: BrandService,
    private categoryService: CategoryService,
    private router: Router,
    private layoutService: LayoutService,
    private route: ActivatedRoute
  ) {
    if (this.layoutService.isMobile$.value) {
      this.layoutService.setFooterVisibility(false);
    } else {
      this.layoutService.setFooterVisibility(true);
    }
    if (this.router.url.includes('/shop')) this.isShop = true;
    this.route.queryParams.subscribe((params) => {
      this.currentPage = Number(params['page'] ? params['page'] : 1);
      if (params['sale']) this.sale = Boolean(params['sale']);
      if (params['category'] && this.isShop)
        this.selectedCategoryId = params['category'];
      if (params['categories']) {
        this.selectedCategorySlugs = [];
        params['categories']
          .split(',')
          .forEach((slug: string) => this.selectedCategorySlugs.push(slug));
      }
      if (params['brands']) {
        this.selectedBrandIds = [];
        params['brands']
          .split(',')
          .forEach((brandId: number) =>
            this.selectedBrandIds.push({ id: Number(brandId) })
          );
      }
      if (params['minPrice'])
        this.selectedPriceRange.minPrice = params['minPrice'];
      if (params['maxPrice'])
        this.selectedPriceRange.maxPrice = params['maxPrice'];

      if (!this.isShop) this.loadProducts(); // prevent load product when waiting shopSlug to be inputted
      if (this.shopSlug) this.loadProducts(); // load product if isShop is true when waiting shopSlug to be inputted
    });
  }
  ngOnInit(): void {
    forkJoin([
      this.brandService.getBrands(),
      !this.shopSlug
        ? this.categoryService.getCategories()
        : this.categoryService.getShopCategories(this.shopSlug),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([brands, resCategory]: [Brand[], any]) => {
        this.brands = brands;
        this.categories = resCategory.categories;
        this.setActiveCategory();
      });
    this.loadProducts();
  }
  ngOnChanges() {
    if (this.isShop) this.loadProducts();
  }
  ngOnDestroy(): void {
    this.layoutService.setFooterVisibility(true);
    this.destroy$.next();
    this.destroy$.complete();
  }

  // event handlers
  loadProducts() {
    if (!this.loading) {
      if (this.layoutService.isMobile.value && this.onShowMore)
        this.mCurrentPage = Number(this.mCurrentPage) + 1; // to avoid increased mobile current page when user scroll page to show more
      this.loading = true;
      this.productService
        .getProducts(this.currenDataListParameter)
        .pipe(takeUntil(this.destroy$))
        .subscribe((res: any) => {
          this.filteredItems = res.filteredItems;
          this.totalProducts = Number(res.totalProducts);
          this.loading = false;
          if (this.onShowMore) {
            this.onShowMore = false;
            res.products.forEach((product: Product) =>
              this.products.push(product)
            );
          } else {
            this.onScrollToProductListTop();
            this.products = res.products;
          }
        });
    }
  }
  setActiveCategory() {
    let category1 = this.selectedCategorySlugs[0];
    let category2 = this.selectedCategorySlugs[1];
    let selectedCategory1 = this.categories.find(
      (x: Category) => x.slug == category1
    );
    if (selectedCategory1) {
      selectedCategory1.show_children = true;
    }
    if (category2) {
      let selectedCategory2 = selectedCategory1?.children.find(
        (x: Category) => x.slug == category2
      );
      if (selectedCategory2) {
        selectedCategory2.show_children = true;
      }
    }
  }
  get currenDataListParameter(): ProductDataListParameter {
    let productDataListParameter: ProductDataListParameter = {
      item_per_page: 30,
      current_page: this.currentPage,
      filters: this.filters,
      sorts: this.sorts,
      shop_slug: this.shopSlug,
      category_id: this.selectedCategoryId,
      category_slug:
        this.selectedCategorySlugs[this.selectedCategorySlugs.length - 1],
      brands: this.selectedBrandIds,
      min_price: this.selectedPriceRange.minPrice,
      max_price: this.selectedPriceRange.maxPrice,
      search_query: null,
      show_promotion_only: false,
      foundation_shade_colors: null,
      sale: this.sale,
    };
    if (
      this.selectedCategoryId == null &&
      this.selectedCategorySlugs.length < 1
    ) {
      delete productDataListParameter.category_id;
      delete productDataListParameter.category_slug;
    }
    return productDataListParameter;
  }

  fixedFilter = false;
  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (this.layoutService.isMobile.value) {
      let detailBoxBounds =
        this.productList?.nativeElement.getBoundingClientRect();
      if (detailBoxBounds.top <= 0) {
        this.fixedFilter = true;
      } else {
        this.fixedFilter = false;
      }
      // show more scroll mobile
      let mFooterHeight = 0;
      let mProductListBounds =
        this.productList?.nativeElement.getBoundingClientRect();
      if (
        window.pageYOffset + window.innerHeight >
        mProductListBounds.height - mFooterHeight
      ) {
        if (this.totalProducts > 30 * this.mCurrentPage) {
          // to avoid increased mobile current page when user scroll page to show more
          this.onShowMore = true;
          this.loadProducts();
        }
      }
    }
  }

  onScrollToProductListTop() {
    let headerOffset = 200;
    let offsetPosition =
      this.productListContainer?.nativeElement.offsetTop - headerOffset;
    window.scrollTo({ top: offsetPosition });
  }

  setSaleFilter(data: any) {
    this.resetCurrentPage();
    this.sale = data.sale;
    this.setupRouterParam();
  }
  // category filter
  setCategoryFilter(event: any) {
    this.resetCurrentPage();
    this.selectedCategorySlugs = event.slugs;
    // this.selectedCategoryId = event.id;
    this.setupRouterParam();
  }
  removeCategoryFilter() {
    this.resetCurrentPage();
    this.selectedCategoryId = undefined;
    this.selectedCategorySlugs = [];
    this.setupRouterParam();
  }
  // brand filter
  setBrandFilter(brandIds: any) {
    this.resetCurrentPage();
    this.selectedBrandIds = [];
    if (brandIds && brandIds.length > 0) this.selectedBrandIds = brandIds;
    if (isPlatformBrowser(this.platformId)) {
      clearInterval(this.brandSelectionInterval);
      this.brandSelectionInterval = setInterval(() => {
        // delay on rapid brands selection
        this.setupRouterParam();
        clearInterval(this.brandSelectionInterval);
      }, 1000);
    }
  }
  removeBrandFilter(brandId: number) {
    this.resetCurrentPage();
    this.selectedBrandIds = this.selectedBrandIds.filter(
      (x: any) => x.id != brandId
    );
    this.filteredItems['brands'] = this.filteredItems['brands'].filter(
      (x: any) => x.id != brandId
    );
    if (isPlatformBrowser(this.platformId)) {
      clearInterval(this.brandSelectionInterval);
      this.brandSelectionInterval = setInterval(() => {
        // delay on rapid brands selection
        this.setupRouterParam();
        clearInterval(this.brandSelectionInterval);
      }, 1500);
    }
  }
  // price filter
  setPriceFilter(priceRange: any) {
    this.resetCurrentPage();
    this.selectedPriceRange.minPrice = priceRange.minPrice;
    this.selectedPriceRange.maxPrice = priceRange.maxPrice;
    this.setupRouterParam();
  }
  removeMaxPriceFilter() {
    this.resetCurrentPage();
    this.selectedPriceRange.maxPrice = null;
    this.setupRouterParam();
  }
  removeMinPriceFilter() {
    this.resetCurrentPage();
    this.selectedPriceRange.minPrice = null;
    this.setupRouterParam();
  }

  // setup router and parameters
  setupRouterParam() {
    // set page params
    this.currentPage > 1
      ? Object.assign(this.queryParams, { page: this.currentPage })
      : delete this.queryParams['page'];

    if (this.sale) {
      Object.assign(this.queryParams, { sale: this.sale });
    } else {
      delete this.queryParams['sale'];
    }
    // set brands params
    if (this.selectedBrandIds.length > 0) {
      let brandIdsList = this.selectedBrandIds
        .map((selectedBrand: any) => selectedBrand.id)
        .join(',');
      Object.assign(this.queryParams, { brands: brandIdsList });
    } else {
      delete this.queryParams.brands;
    }
    // set category params
    if (this.selectedCategorySlugs.length > 0) {
      let categorySlugs = this.selectedCategorySlugs
        .map((slug: string) => slug)
        .join(',');
      Object.assign(this.queryParams, { categories: categorySlugs });
    } else {
      delete this.queryParams.categories;
    }
    // set price params
    this.selectedPriceRange.minPrice != null
      ? Object.assign(this.queryParams, {
          minPrice: this.selectedPriceRange.minPrice,
        })
      : delete this.queryParams['minPrice'];
    this.selectedPriceRange.maxPrice != null
      ? Object.assign(this.queryParams, {
          maxPrice: this.selectedPriceRange.maxPrice,
        })
      : delete this.queryParams['maxPrice'];
    this.router.navigate(['product'], {
      queryParams: this.queryParams,
    });

    // set filter rating, recomendation, and best selling
    this.filterRating
      ? Object.assign(this.queryParams, { rating: this.filterRating })
      : delete this.queryParams['rating'];
    this.myRecommendation
      ? Object.assign(this.queryParams, {
          recommendation: this.myRecommendation,
        })
      : delete this.queryParams['recommendation'];
    this.bestSelling
      ? Object.assign(this.queryParams, { bestselling: this.bestSelling })
      : delete this.queryParams['bestselling'];

    if (!this.isShop) {
      this.router.navigate(['/product'], {
        queryParams: this.queryParams,
      });
    } else {
      this.selectedCategoryId
        ? Object.assign(this.queryParams, { category: this.selectedCategoryId })
        : delete this.queryParams['category'];
      this.router.navigate(['shop', this.shopSlug], {
        queryParams: this.queryParams,
      });
    }
  }

  resetCurrentPage() {
    this.currentPage = 1;
    delete this.queryParams['page'];
  }
  // manage sort
  setSort(sort: Sort) {
    this.selectedSort = sort;
    if (sort.label == 'best-selling') {
      this.bestSelling = true;
    } else {
      if (sort.field) {
        this.sorts = [{ field: sort.field, direction: sort.direction }];
      } else {
        this.sorts = [];
      }
    }
    this.resetCurrentPage();
    this.setupRouterParam();
    this.loadProducts();
  }
  pageChange(page: number) {
    this.currentPage = Number(page);
    this.setupRouterParam();
  }

  get hasFilter(): boolean {
    if (this.filteredItems) {
      if (
        this.filteredItems.category ||
        this.filteredItems.brands ||
        this.selectedPriceRange.minPrice ||
        this.selectedPriceRange.maxPrice ||
        this.filterRating ||
        this.myRecommendation
      ) {
        return true;
      }
    }
    return false;
  }
  removeAllFilters() {
    this.resetCurrentPage();
    this.selectedCategoryId = undefined;
    this.selectedCategorySlugs = [];
    this.selectedBrandIds = [];
    this.selectedPriceRange = { minPrice: null, maxPrice: null };
    this.filterRating = false;
    this.myRecommendation = false;
    this.setupRouterParam();
  }
}
